import React from 'react';
import { HintData, StockData } from '../../types/game';
import {
  Info,
  Factory,
  Megaphone,
  Crown,
  Package,
  BarChart3,
  MapPin,
  Users,
  Calculator,
  Scale,
  ShoppingBag,
  Star,
  Landmark,
  TrendingUp,
  Shield,
  AlertTriangle,
  Briefcase,
  Eye,
  Award
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { HINT_TYPE_REGISTRY } from '../../config/hintConfig';

/**
 * Map lucide icon names (from hint type registry) to actual icon components.
 */
const iconComponentMap: Record<string, LucideIcon> = {
  Scale,
  ShoppingBag,
  Star,
  Landmark,
  Factory,
  TrendingUp,
  Shield,
  AlertTriangle,
  Crown,
  Briefcase,
  Eye,
  Package,
  Award,
  Info,
  Megaphone,
  BarChart3,
  MapPin,
  Users,
  Calculator,
};

/**
 * Legacy icon map for old hint type strings.
 */
const legacyIconMap: Record<string, LucideIcon> = {
  sector: Factory,
  marketCap: BarChart3,
  location: MapPin,
  founded: Crown,
  employees: Users,
  revenue: Megaphone,
  description: Package,
  financials: Calculator,
};

/**
 * Get the icon component for a hint type.
 * Checks the hint type registry first (new dynamic types),
 * then falls back to the legacy icon map.
 */
function getHintIcon(type: string): LucideIcon {
  // Check registry for dynamic hint types
  const registryEntry = HINT_TYPE_REGISTRY[type];
  if (registryEntry) {
    return iconComponentMap[registryEntry.icon] || Info;
  }
  // Legacy fallback
  return legacyIconMap[type] || Info;
}

interface HintRendererProps {
  hint: HintData;
  stockData: StockData | null;
  isExpanded?: boolean;
  categoryColor?: string;
  hintIndex?: number;
}

/**
 * Unified hint renderer - all hints follow the same simple format:
 * - Category-colored icon badge
 * - Title (from hint.title)
 * - Raw hint text content (from hint.content)
 * - Expandable for long content
 */
export const UnifiedHintRenderer: React.FC<HintRendererProps> = ({
  hint,
  categoryColor = '#3B82F6' // Default to blue if no category color provided
}) => {
  const IconComponent = getHintIcon(hint.type);
  const safeContent = typeof hint.content === 'string' ? hint.content : '';
  const trimmedContent = safeContent.trim();
  const hasContent = trimmedContent.length > 0;
  const fallbackContent = 'Hint details coming soon.';

  // Determine bullet display
  const hasBullets = trimmedContent.includes(' | ') || trimmedContent.includes('|');
  const delimiter = trimmedContent.includes(' | ') ? ' | ' : '|';

  // Get bullet points to display
  const bulletPoints = hasBullets
    ? trimmedContent.split(delimiter).map((point) => point.trim()).filter(Boolean)
    : [];

  const maxBullets = hint.maxVisibleBullets;
  const displayBullets = (maxBullets !== undefined && hasBullets)
    ? bulletPoints.slice(0, maxBullets)
    : bulletPoints;

  return (
    <div
      className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-2 items-start"
      role="group"
      aria-labelledby={`hint-${hint.type}-heading`}
    >
      <div
        className="flex items-center justify-center rounded-[10px]"
        style={{
          width: '36px',
          height: '36px',
          backgroundColor: 'var(--clue-teal-soft)',
        }}
        aria-hidden="true"
      >
        <IconComponent className="w-[18px] h-[18px]" style={{ color: 'var(--clue-teal-accent)' }} />
      </div>
      <h4
        id={`hint-${hint.type}-heading`}
        className="font-serif text-[clamp(1rem,2vw,1.1875rem)] font-normal leading-tight self-end pb-1"
        style={{ color: 'var(--clue-text-primary)' }}
      >
        {hint.title}
      </h4>
      {/* Render as bullet points if delimited, otherwise as paragraph */}
      {hasBullets && displayBullets.length > 0 ? (
        <ul
          className="col-span-2 list-none text-[clamp(0.8rem,1.5vw,0.9375rem)] leading-relaxed mt-1 space-y-1"
          aria-label={`Hint: ${displayBullets.join(', ')}`}
        >
          {displayBullets.map((point, index) => (
            <li key={index} className="relative pl-5 text-black dark:text-white">
              <span
                className="absolute left-0 top-1 text-sm font-bold"
                style={{ color: 'var(--clue-teal-accent)' }}
                aria-hidden="true"
              >
                →
              </span>
              <span className="inline">{point}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p
          className="col-span-2 text-[clamp(0.8rem,1.5vw,0.9375rem)] leading-relaxed mt-1 text-black dark:text-white"
          style={{ lineHeight: 1.6 }}
          aria-label={`Hint: ${hasContent ? trimmedContent : fallbackContent}`}
        >
          {hasContent ? trimmedContent : fallbackContent}
        </p>
      )}
      <div className="sr-only">
        Complete hint: {displayBullets.length > 0 ? displayBullets.join(', ') : (hasContent ? trimmedContent : fallbackContent)}
      </div>
    </div>
  );
};

// Main hint content renderer that uses the unified renderer for all hints
export const HintContentRenderer: React.FC<HintRendererProps> = ({ hint, stockData, isExpanded = false, categoryColor, hintIndex }) => {
  return <UnifiedHintRenderer hint={hint} stockData={stockData} isExpanded={isExpanded} categoryColor={categoryColor} hintIndex={hintIndex} />;
};
